import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Button, MenuItem, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { selectUser } from "../../../../features/userSlice";
import DataTable from "../../../../components/WindowDoor/ServiceRequests/DataTable";
import {
  STATUS_RAIL,
  addressLabel,
  customerLabel,
  daysActive,
  isOpen,
  shortDate,
} from "../../../../components/WindowDoor/ServiceRequests/constants";
import { fetchServiceRequests } from "../../../../components/WindowDoor/ServiceRequests/api";
import { StatusChip } from "./ServiceRequestShared";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import { PageHeader, PageShell } from "../../../../components/UI/Page";

/**
 * A salesperson's own service requests. Scoped server-side by userID AND
 * email, so requests submitted through the old PHP form (which only recorded
 * the salesperson's email) still show up here.
 */
const MyServiceRequests = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  const [requests, setRequests] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [error, setError] = useState(null);

  useEffect(() => {
    // Pages can mount before login finishes; wait for the user.
    if (!user?.email && !user?.userID) return;
    fetchServiceRequests({ scope: "mine", status: "all", user })
      .then((data) => setRequests(Array.isArray(data) ? data : []))
      .catch((err) => {
        setRequests([]);
        setError(err.message || "Could not load your service requests.");
      });
  }, [user?.email, user?.userID]);

  const visible = useMemo(() => {
    const all = requests || [];
    if (statusFilter === "open") return all.filter((row) => isOpen(row.status));
    if (statusFilter === "all") return all;
    return all.filter((row) => row.status === statusFilter);
  }, [requests, statusFilter]);

  const openCount = (requests || []).filter((row) => isOpen(row.status)).length;

  return (
    <PageShell size="full">
      <PageHeader
        icon={HomeRepairServiceOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title="My Service Requests"
        description={`${openCount} open. Open one to see where the shop has it and to message them.`}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/windowdoor/service-request")}
          >
            New Request
          </Button>
        }
      />

      {error && (
        <Alert severity="error" className="!mb-4">
          {error}
        </Alert>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="all">All statuses</MenuItem>
          {STATUS_RAIL.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
      </div>

        <DataTable
          rows={visible}
          allRows={requests || []}
          loading={requests === null}
          onRowClick={(row) =>
            navigate(`/windowdoor/my-service-requests/${row._id}`)
          }
          initialSort={{ key: "entry", dir: "desc" }}
          searchPlaceholder="Search customer, address, status…"
          emptyTitle="No service requests"
          emptyBody='Nothing here yet — use "New Request" to send one to the window/door shop.'
          columns={[
            {
              key: "entry",
              title: "Entry",
              sortValue: (row) => Number(row.entry_number) || 0,
              searchValue: (row) => row.entry_number,
              render: (row) => (
                <div className="flex flex-col">
                  <span className="app-accent font-semibold tabular-nums">
                    {row.entry_number}
                  </span>
                  <span className="app-muted whitespace-nowrap text-xs">
                    {shortDate(row.entry_date)}
                  </span>
                </div>
              ),
            },
            {
              key: "customer",
              title: "Customer",
              sortValue: (row) => customerLabel(row),
              searchValue: (row) =>
                `${customerLabel(row)} ${row.manufacturer || ""}`,
              render: (row) => (
                <div className="flex flex-col">
                  <span className="font-medium">{customerLabel(row)}</span>
                  <span className="app-muted text-xs">
                    {row.manufacturer || "—"}
                  </span>
                </div>
              ),
            },
            {
              key: "address",
              title: "Address",
              sortValue: (row) => addressLabel(row),
              searchValue: (row) => addressLabel(row),
              render: (row) => (
                <span className="app-muted block break-words">
                  {addressLabel(row) || "—"}
                </span>
              ),
            },
            {
              key: "status",
              title: "Status",
              sortValue: (row) => row.status || "",
              searchValue: (row) => row.status,
              render: (row) => {
                const days = daysActive(row);
                return (
                  <div className="flex flex-col items-start gap-1">
                    <StatusChip status={row.status} />
                    {isOpen(row.status) && (
                      <span className="app-muted text-xs tabular-nums">
                        {days} day{days === 1 ? "" : "s"} open
                      </span>
                    )}
                  </div>
                );
              },
            },
            {
              key: "assignee",
              title: "Assigned To",
              sortValue: (row) => row.assigned_to?.name || "",
              searchValue: (row) => row.assigned_to?.name,
              render: (row) =>
                row.assigned_to?.name || (
                  <span className="app-muted">Not yet assigned</span>
                ),
            },
          ]}
        />
    </PageShell>
  );
};

export default MyServiceRequests;
